// Users — cross-org account search.
//
// The pre-existing API can only list users one org at a time, which is the
// wrong shape for the question actually asked here ("who is deepak@…?"). This
// screen is built on /admin/users, where a partial email is enough and the org
// comes back with the row.

import React from 'react';
import { api, errorText } from '../api';
import { useAsync, useDebounced } from '../useAsync';
import { navigate } from '../router';
import { num } from '../format';
import UserSheet from '../ui/UserSheet';
import {
  Button, Chips, Empty, ErrorState, PageHead, Pill, RefreshButton, Row, Search,
  Skeleton,
} from '../ui/primitives';

const STATUS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Deactivated' },
  { value: 'unbound', label: 'Never signed in' },
];

const PAGE = 50;

export default function Users({ query }) {
  const [q, setQ] = React.useState(query.q || '');
  const [status, setStatus] = React.useState(query.status || 'all');
  const [page, setPage] = React.useState(0);
  const [openUser, setOpenUser] = React.useState(null);
  const debouncedQ = useDebounced(q, 280);

  // A changed filter must reset paging, or page 3 of the old query silently
  // becomes an empty page 3 of the new one.
  React.useEffect(() => { setPage(0); }, [debouncedQ, status]);

  const { data, error, loading, at, reload } = useAsync(
    () => api.users({ q: debouncedQ, status, limit: PAGE, offset: page * PAGE }),
    [debouncedQ, status, page]);

  const shown = data ? page * PAGE + data.items.length : 0;

  return (
    <>
      <PageHead title="Users"
                sub={data ? `${num(data.total)} account${data.total === 1 ? '' : 's'} across all orgs` : 'Every account in Mono'}
                action={<RefreshButton onClick={reload} loading={loading} at={at} />} />

      <div className="stack" style={{ marginBottom: 14 }}>
        <Search value={q} onChange={setQ} placeholder="Search email or name" />
        <Chips options={STATUS} value={status} onChange={setStatus} />
      </div>

      {error && <ErrorState error={error} text={errorText(error)} onRetry={reload} />}
      {!data && loading && <Skeleton rows={5} />}

      {data && data.items.length === 0 && (
        <Empty title="No users">
          {q ? <>Nothing matches “{q}”. Users are provisioned inside an org — try{' '}
            <button className="btn ghost small" onClick={() => navigate('/orgs')}>Orgs</button>.</>
            : 'No accounts match these filters.'}
        </Empty>
      )}

      {data && data.items.length > 0 && (
        <div className="card">
          <div className="list">
            {data.items.map((u) => (
              <Row key={u.id} onClick={() => setOpenUser(u.id)}>
                <div className="col" style={{ flex: 1 }}>
                  <div className="row" style={{ gap: 6 }}>
                    <span className="truncate">{u.display_name || u.email}</span>
                    {!u.is_active && <Pill tone="danger">off</Pill>}
                    {!u.sso_bound && <Pill tone="warn">unbound</Pill>}
                    {u.is_superuser && <Pill tone="accent">su</Pill>}
                  </div>
                  <div className="dim truncate" style={{ fontSize: 11.5 }}>
                    <span className="mono">{u.email}</span> · {u.org_name || u.org_id}
                  </div>
                </div>
                <Pill>{u.role}</Pill>
              </Row>
            ))}
          </div>
        </div>
      )}

      {data && data.total > PAGE && (
        <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
          <Button small disabled={page === 0 || loading}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
          <span className="mono dim" style={{ fontSize: 11 }}>
            {page * PAGE + 1}–{shown} of {num(data.total)}
          </span>
          <Button small disabled={shown >= data.total || loading}
                  onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      {openUser && (
        <UserSheet userId={openUser} onClose={() => setOpenUser(null)} onChanged={reload} />
      )}
    </>
  );
}
